/**
 * Texts of the admin "Field collection" page. The page title is the menu
 * label, so both read from here.
 */
export const fieldCollectionCopy = {
  fr: {
    navLabel: "Collecte terrain",
    description:
      "Suivez les collectes menées sur le terrain par les collecteurs de vos langues. Lecture seule : les sessions restent menées par les collecteurs.",
    periodLabel: "Période",
    // Overview
    overviewTotals: "Tous les collecteurs, sur la période",
    statProfiles: "Profils",
    statContributions: "Contributions",
    statContributionsHint: (accepted: string, rejected: string) => `${accepted} validées · ${rejected} rejetées`,
    statRecorded: "Temps enregistré",
    statPaid: "Versé",
    statPaidHint: (outstanding: string) => `${outstanding} à payer`,
    errorTotals: "Impossible de charger les totaux de la période.",
    errorAgeSex: "Impossible de charger la répartition par âge et par sexe.",
    collectorsTitle: "Collecteurs",
    collectorsCount: (count: string) => `${count} collecteur${count === "1" ? "" : "s"}`,
    searchLabel: "Rechercher un collecteur",
    searchPlaceholder: "Nom, identifiant ou téléphone",
    clearSearch: "Effacer la recherche",
    colCollector: "Collecteur",
    colLanguages: "Langues",
    colProfiles: "Profils",
    colContributions: "Contributions",
    colMinutes: "Minutes enregistrées",
    colPaid: "Versé",
    colLastActivity: "Dernière activité",
    sortBy: (column: string) => `Trier par ${column.toLowerCase()}`,
    never: "Jamais",
    justNow: "à l'instant",
    noLanguage: "Aucune langue",
    openCollector: (name: string) => `Voir le suivi de ${name}`,
    loadingCollectors: "Chargement des collecteurs…",
    errorCollectors: "Impossible de charger la liste des collecteurs.",
    noResultsTitle: (q: string) => `Aucun collecteur ne correspond à « ${q} »`,
    noResultsDescription: "Vérifiez l'orthographe ou cherchez par identifiant ou numéro de téléphone.",
    emptyPage: "Cette page est vide.",
    firstPage: "Revenir à la première page",
    previous: "Précédent",
    next: "Suivant",
    pageOf: (page: string, pages: string) => `Page ${page} sur ${pages}`,
    // Detail
    allCollectors: "Tous les collecteurs",
    collectorFallback: (id: string) => `Collecteur n° ${id}`,
    summaryAllTime: "Depuis le début",
    statToday: "Aujourd'hui",
    statFeedback: "Rejetées",
    errorCollector: "Impossible de charger les chiffres de ce collecteur.",
    outOfScope: "Ce collecteur n'est pas dans votre périmètre. Revenez à la liste pour en choisir un autre.",
    // Shared
    loading: "Chargement de la collecte terrain…",
    retry: "Réessayer",
    emptyTitle: "Aucun collecteur sur vos langues",
    emptyDescription:
      "Dès qu'un collecteur sera inscrit sur une des langues qui vous sont ouvertes, son suivi apparaîtra ici.",
    noAccessTitle: "Vous n'avez accès à aucune langue de collecte",
    noAccessDescription:
      "Le suivi des collecteurs s'ouvre langue par langue. Un super admin vous l'ouvre dans Utilisateurs : sur votre ligne, « Langues gérées », puis « Peut voir les collecteurs » pour chaque langue voulue.",
    ageSexTitle: "Répartition par âge et par sexe",
    women: "Femmes",
    men: "Hommes",
    others: "Autres",
    noChartData: "Aucun profil communautaire pour l'instant.",
  },
  en: {
    navLabel: "Field collection",
    description:
      "Follow the field collection run by the collectors of your languages. Read only: sessions stay with the collectors.",
    periodLabel: "Period",
    overviewTotals: "All collectors, over the period",
    statProfiles: "Profiles",
    statContributions: "Contributions",
    statContributionsHint: (accepted: string, rejected: string) => `${accepted} accepted · ${rejected} rejected`,
    statRecorded: "Recorded time",
    statPaid: "Paid",
    statPaidHint: (outstanding: string) => `${outstanding} outstanding`,
    errorTotals: "The totals for the period could not be loaded.",
    errorAgeSex: "The age and sex breakdown could not be loaded.",
    collectorsTitle: "Collectors",
    collectorsCount: (count: string) => `${count} collector${count === "1" ? "" : "s"}`,
    searchLabel: "Search a collector",
    searchPlaceholder: "Name, username or phone",
    clearSearch: "Clear search",
    colCollector: "Collector",
    colLanguages: "Languages",
    colProfiles: "Profiles",
    colContributions: "Contributions",
    colMinutes: "Recorded minutes",
    colPaid: "Paid",
    colLastActivity: "Last activity",
    sortBy: (column: string) => `Sort by ${column.toLowerCase()}`,
    never: "Never",
    justNow: "just now",
    noLanguage: "No language",
    openCollector: (name: string) => `Open ${name}'s tracking`,
    loadingCollectors: "Loading collectors…",
    errorCollectors: "The collector list could not be loaded.",
    noResultsTitle: (q: string) => `No collector matches "${q}"`,
    noResultsDescription: "Check the spelling or search by username or phone number.",
    emptyPage: "This page is empty.",
    firstPage: "Back to the first page",
    previous: "Previous",
    next: "Next",
    pageOf: (page: string, pages: string) => `Page ${page} of ${pages}`,
    allCollectors: "All collectors",
    collectorFallback: (id: string) => `Collector #${id}`,
    summaryAllTime: "All time",
    statToday: "Today",
    statFeedback: "Rejected",
    errorCollector: "This collector's figures could not be loaded.",
    outOfScope: "This collector is outside your scope. Go back to the list to pick another one.",
    loading: "Loading field collection…",
    retry: "Retry",
    emptyTitle: "No collector in your languages",
    emptyDescription:
      "As soon as a collector signs up for one of the languages open to you, their tracking shows up here.",
    noAccessTitle: "You have no access to any collection language",
    noAccessDescription:
      "Collector tracking opens language by language. A super admin opens it in Users: on your row, \"Managed languages\", then \"Can see collectors\" for each language.",
    ageSexTitle: "Breakdown by age and sex",
    women: "Women",
    men: "Men",
    others: "Others",
    noChartData: "No community profile yet.",
  },
} as const;

export type FieldCollectionCopy = (typeof fieldCollectionCopy)["fr"] | (typeof fieldCollectionCopy)["en"];

export const fieldCollectionCopyFor = (lang: string | undefined): FieldCollectionCopy =>
  lang === "en" ? fieldCollectionCopy.en : fieldCollectionCopy.fr;
